import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../../services/api';
import { KGVersion } from '../../types';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateTime } from '../../utils/dateUtils';
import CreateVersionModal from './CreateVersionModal';
import TypedConfirmModal from '../TypedConfirmModal';
import '../shared.css';
import './KGDetails.css';

/* ── Inline SVG icons (no emoji, no third-party sets) ───────────────────── */
function EditIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}
function GDSIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M6.34 6.34A8 8 0 1 0 17.66 17.66" />
      <path d="M12 2v4M12 18v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83" />
    </svg>
  );
}
function DotsIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2" />
      <circle cx="12" cy="12" r="2" />
      <circle cx="19" cy="12" r="2" />
    </svg>
  );
}
function SearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/* ── Row overflow menu ───────────────────────────────────────────────────── */
interface RowMenuProps {
  versionId: string;
  published: boolean;
  onPublishToggle: () => void;
  onDelete: () => void;
}

function RowMenu({ versionId, published, onPublishToggle, onDelete }: RowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div className="vt-row-menu" ref={ref}>
      <button
        className="vt-menu-trigger"
        onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
        aria-label="More actions"
        aria-haspopup="true"
        aria-expanded={open}
      >
        <DotsIcon />
      </button>
      {open && (
        <div className="vt-menu-popover" role="menu">
          <button
            className="vt-menu-item"
            role="menuitem"
            onClick={() => { setOpen(false); onPublishToggle(); }}
          >
            {published ? 'Unpublish' : 'Publish'}
          </button>
          <div className="vt-menu-sep" role="separator" />
          <button
            className="vt-menu-item vt-menu-item--danger"
            role="menuitem"
            onClick={() => { setOpen(false); onDelete(); }}
          >
            Delete…
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main component ──────────────────────────────────────────────────────── */
interface KGDetailsProps {
  hasGdsLicense: boolean;
}

const KGDetails: React.FC<KGDetailsProps> = ({ hasGdsLicense }) => {
  const { kgId } = useParams<{ kgId: string }>();
  const navigate = useNavigate();
  const [versions, setVersions] = useState<KGVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingVersionId, setEditingVersionId] = useState<string | null>(null);
  const [editedDescription, setEditedDescription] = useState('');
  const [editingDataVersionId, setEditingDataVersionId] = useState<string | null>(null);
  const [editedDataVersion, setEditedDataVersion] = useState('');
  const [dataVersionError, setDataVersionError] = useState<Record<string, string>>({});
  const [autoScalingToggling, setAutoScalingToggling] = useState<Record<string, boolean>>({});
  const [versionsWithInstances, setVersionsWithInstances] = useState<Set<string>>(new Set());
  const [confirmDeleteVersionId, setConfirmDeleteVersionId] = useState<string | null>(null);

  const loadIndexCounts = useCallback(async (_kgIdArg: string, _versionIds: string[]) => {
    // index counts removed from this view — kept for future use
  }, []);

  const loadVersions = useCallback(async () => {
    if (!kgId) return;
    try {
      setLoading(true);
      setError('');
      const response: any = await apiService.listVersions(kgId);
      if (response.data) {
        const versionList = response.data.map((item: any) => ({
          kg_id: item.kg_id || kgId,
          version_id: item.version_id,
          kg_name: item.kg_name || item.attributes?.kg_name || 'Unnamed KG',
          kg_description: item.kg_description || item.attributes?.kg_description,
          created_at: item.created_at || item.attributes?.created_at,
          published: item.published ?? item.attributes?.published ?? false,
          org_id: item.org_id || item.attributes?.org_id,
          auto_scaling_enabled: item.auto_scaling_enabled ?? item.attributes?.auto_scaling_enabled ?? false,
          gds_enabled: item.gds_enabled ?? item.attributes?.gds_enabled ?? false,
          data_version: item.data_version || item.attributes?.data_version || undefined,
          created_by: item.created_by || item.attributes?.created_by || undefined,
          created_by_email: item.created_by_email || item.attributes?.created_by_email || undefined,
          data_files: item.data_files || item.attributes?.data_files,
        }));
        setVersions(versionList);
        // Cache org_id for the sidebar
        const firstOrgId = versionList[0]?.org_id;
        if (firstOrgId) {
          sessionStorage.setItem('polly_org_id', String(firstOrgId));
          window.dispatchEvent(new Event('pollyOrgIdUpdated'));
        }
        loadInstanceCounts(kgId);
      }
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to load versions'));
    } finally {
      setLoading(false);
    }
  }, [kgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInstanceCounts = useCallback(async (kgIdArg: string) => {
    try {
      const response: any = await apiService.getAllInstances();
      if (response.data) {
        const withInstances = new Set<string>(
          response.data
            .filter((inst: any) => inst.kg_id === kgIdArg && inst.is_terminated !== true)
            .map((inst: any) => String(inst.version_id))
        );
        setVersionsWithInstances(withInstances);
      }
    } catch {
      // non-critical
    }
  }, []);

  const handlePublishToggle = useCallback(async (versionId: string, currentPublished: boolean) => {
    if (!kgId) return;
    try {
      await apiService.publishVersion(kgId, versionId, !currentPublished);
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update version'));
    }
  }, [kgId, loadVersions]);

  const handleDeleteVersion = useCallback((versionId: string) => {
    setConfirmDeleteVersionId(versionId);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!kgId || !confirmDeleteVersionId) return;
    const versionId = confirmDeleteVersionId;
    setConfirmDeleteVersionId(null);
    try {
      await apiService.deleteVersion(kgId, versionId);
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to delete version'));
    }
  }, [kgId, confirmDeleteVersionId, loadVersions]);

  const handleVersionCreated = useCallback(() => {
    setShowCreateModal(false);
    loadVersions();
  }, [loadVersions]);

  const handleStartEditDescription = useCallback((versionId: string, current: string) => {
    setEditingVersionId(versionId);
    setEditedDescription(current || '');
  }, []);

  const handleCancelEditDescription = useCallback(() => {
    setEditingVersionId(null);
    setEditedDescription('');
  }, []);

  const handleSaveDescription = useCallback(async (versionId: string) => {
    if (!kgId) return;
    try {
      await apiService.updateVersionDescription(kgId, versionId, editedDescription);
      setEditingVersionId(null);
      setEditedDescription('');
      loadVersions();
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update description'));
    }
  }, [kgId, editedDescription, loadVersions]);

  const handleStartEditDataVersion = useCallback((versionId: string, current: string) => {
    setEditingDataVersionId(versionId);
    setEditedDataVersion(current || '');
    setDataVersionError(prev => ({ ...prev, [versionId]: '' }));
  }, []);

  const handleCancelEditDataVersion = useCallback(() => {
    setEditingDataVersionId(null);
    setEditedDataVersion('');
  }, []);

  const handleSaveDataVersion = useCallback(async (versionId: string) => {
    if (!kgId) return;
    const trimmed = editedDataVersion.trim();
    if (!trimmed) {
      setDataVersionError(prev => ({ ...prev, [versionId]: 'Must be a non-empty string' }));
      return;
    }
    try {
      await apiService.updateVersionDataVersion(kgId, versionId, trimmed);
      setEditingDataVersionId(null);
      setEditedDataVersion('');
      setDataVersionError(prev => ({ ...prev, [versionId]: '' }));
      loadVersions();
    } catch (err: any) {
      const msg = extractErrorMessage(err, 'Failed to update data version');
      setDataVersionError(prev => ({ ...prev, [versionId]: msg }));
    }
  }, [kgId, editedDataVersion, loadVersions]);

  const handleAutoScalingToggle = useCallback(async (versionId: string, currentEnabled: boolean) => {
    if (!kgId) return;
    setAutoScalingToggling(prev => ({ ...prev, [versionId]: true }));
    try {
      await apiService.updateVersionAutoScaling(kgId, versionId, !currentEnabled);
      setVersions(prev => prev.map(v =>
        v.version_id === versionId ? { ...v, auto_scaling_enabled: !currentEnabled } : v
      ));
    } catch (err: any) {
      setError(extractErrorMessage(err, 'Failed to update auto scaling'));
    } finally {
      setAutoScalingToggling(prev => ({ ...prev, [versionId]: false }));
    }
  }, [kgId]);

  useEffect(() => {
    if (kgId) loadVersions();
  }, [kgId, loadVersions]);

  if (loading) return (
    <div className="kgd-surface">
      <div className="page-loader"><div className="page-spinner" /></div>
    </div>
  );

  const kgName = versions[0]?.kg_name || kgId;

  const filteredVersions = versions
    .filter((v) => {
      const s = searchTerm.toLowerCase();
      return v.version_id?.toString().toLowerCase().includes(s)
        || v.kg_description?.toLowerCase().includes(s)
        || v.created_by?.toLowerCase().includes(s)
        || v.created_by_email?.toLowerCase().includes(s)
        || (v.published ? 'published' : 'unpublished').includes(s);
    })
    .slice()
    .sort((a, b) => Number(b.version_id) - Number(a.version_id));

  const publishedCount   = versions.filter(v => v.published).length;
  const unpublishedCount = versions.length - publishedCount;

  return (
    <div className="kgd-surface">

      {/* ── Page title (outside card) ── */}
      <div className="kgd-page-header">
        <div>
          <h1 className="kgd-page-title">{kgName}</h1>
          <p className="kgd-page-id">
            <span className="kgd-page-id-badge">{kgId}</span>
          </p>
        </div>
        <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
          + Create new version
        </button>
      </div>

      {error && <div className="error-message" style={{ marginBottom: 12 }}>{error}</div>}

      {/* ── White card ── */}
      <div className="kgd-card">

        {/* Card toolbar */}
        <div className="kgd-card-toolbar">
          {/* Summary counts */}
          <div className="kgd-counts">
            <span className="kgd-count-total">
              {versions.length} version{versions.length !== 1 ? 's' : ''}
            </span>
            {versions.length > 0 && (
              <>
                <span className="kgd-count-sep" />
                <span className="kgd-count-stat">
                  <span className="kgd-status-dot kgd-status-dot--published" />
                  Published: <strong>{publishedCount}</strong>
                </span>
                <span className="kgd-count-sep" />
                <span className="kgd-count-stat">
                  <span className="kgd-status-dot kgd-status-dot--draft" />
                  Draft: <strong>{unpublishedCount}</strong>
                </span>
              </>
            )}
          </div>

          {/* Search */}
          <div className="kgd-search-wrap">
            <SearchIcon />
            <input
              type="text"
              className="kgd-search"
              placeholder="Search versions…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Table region */}
        {versions.length === 0 ? (
          <div className="kgd-empty">
            <p>No versions found.</p>
            <button className="tab-btn tab-btn--primary" onClick={() => setShowCreateModal(true)}>
              Create first version
            </button>
          </div>
        ) : filteredVersions.length === 0 ? (
          <div className="kgd-empty"><p>No versions match your search.</p></div>
        ) : (
          <div className="kgd-table-scroll">
            <table className="kgd-table">
              <colgroup>
                <col className="kgd-col-ver" />
                <col className="kgd-col-desc" />
                <col className="kgd-col-dv" />
                <col className="kgd-col-date" />
                <col className="kgd-col-email" />
                <col className="kgd-col-scale" />
                <col className="kgd-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th>Version</th>
                  <th>Description</th>
                  <th>Data version</th>
                  <th>Created at</th>
                  <th>Created by</th>
                  <th>Auto scale</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {filteredVersions.map((version) => (
                  <tr key={version.version_id} className="kgd-row">

                    {/* Version + status pill + GDS meta */}
                    <td className="kgd-cell-ver">
                      <div className="kgd-ver-row">
                        <span className="kgd-ver-num">v{version.version_id}</span>
                        <span className={`kgd-status-pill kgd-status-pill--${version.published ? 'published' : 'draft'}`}>
                          {version.published ? 'PUBLISHED' : 'DRAFT'}
                        </span>
                      </div>
                      {version.gds_enabled && (
                        <div className="kgd-gds-meta">
                          <GDSIcon />
                          <span>GDS enabled</span>
                        </div>
                      )}
                    </td>

                    {/* Description — inline editable */}
                    <td className="kgd-cell-flex">
                      {editingVersionId === version.version_id ? (
                        <div className="kgd-inline-edit">
                          <textarea
                            className="kgd-textarea"
                            value={editedDescription}
                            onChange={(e) => setEditedDescription(e.target.value)}
                            placeholder="Enter description"
                            autoFocus
                            rows={2}
                          />
                          <div className="kgd-edit-actions">
                            <button className="tab-btn tab-btn--primary" onClick={() => handleSaveDescription(version.version_id)}>Save</button>
                            <button className="tab-btn tab-btn--ghost" onClick={handleCancelEditDescription}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="kgd-truncate-wrap">
                          <span
                            className={`kgd-truncate${version.kg_description ? '' : ' kgd-truncate--empty'}`}
                            title={version.kg_description || undefined}
                          >
                            {version.kg_description || <em>No description</em>}
                          </span>
                          <button
                            className="kgd-edit-btn"
                            onClick={() => handleStartEditDescription(version.version_id, version.kg_description || '')}
                            title="Edit description"
                          >
                            <EditIcon />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Data version — inline editable */}
                    <td className="kgd-cell-flex">
                      {editingDataVersionId === version.version_id ? (
                        <div className="kgd-inline-edit">
                          <input
                            className={`kgd-dv-input${dataVersionError[version.version_id] ? ' kgd-dv-input--error' : ''}`}
                            type="text"
                            value={editedDataVersion}
                            onChange={(e) => setEditedDataVersion(e.target.value)}
                            placeholder="e.g. v1.0"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveDataVersion(version.version_id);
                              if (e.key === 'Escape') handleCancelEditDataVersion();
                            }}
                          />
                          {dataVersionError[version.version_id] && (
                            <div className="kgd-field-error">{dataVersionError[version.version_id]}</div>
                          )}
                          <div className="kgd-edit-actions">
                            <button className="tab-btn tab-btn--primary" onClick={() => handleSaveDataVersion(version.version_id)}>Save</button>
                            <button className="tab-btn tab-btn--ghost" onClick={handleCancelEditDataVersion}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="kgd-truncate-wrap">
                          <span className={`kgd-dv-text${version.data_version ? '' : ' kgd-truncate--empty'}`}>
                            {version.data_version || <span className="kgd-em-dash">—</span>}
                          </span>
                          <button
                            className="kgd-edit-btn"
                            onClick={() => handleStartEditDataVersion(version.version_id, version.data_version || '')}
                            title="Edit data version"
                          >
                            <EditIcon />
                          </button>
                        </div>
                      )}
                    </td>

                    {/* Created at */}
                    <td className="kgd-cell-date" title={formatDateTime(version.created_at)}>
                      {formatDateTime(version.created_at)}
                    </td>

                    {/* Created by */}
                    <td className="kgd-cell-flex">
                      {version.created_by_email ? (
                        <span className="kgd-email kgd-truncate" title={version.created_by_email}>
                          {version.created_by_email}
                        </span>
                      ) : version.created_by ? (
                        <span className="kgd-userid kgd-truncate" title={version.created_by}>
                          {version.created_by}
                        </span>
                      ) : (
                        <span className="kgd-em-dash">—</span>
                      )}
                    </td>

                    {/* Auto scale */}
                    <td className="kgd-cell-scale">
                      {versionsWithInstances.has(String(version.version_id)) ? (
                        <label className="kgd-toggle" title="Toggle auto scaling">
                          <input
                            type="checkbox"
                            checked={version.auto_scaling_enabled ?? false}
                            disabled={autoScalingToggling[version.version_id] ?? false}
                            onChange={() => handleAutoScalingToggle(version.version_id, version.auto_scaling_enabled ?? false)}
                          />
                          <span className="kgd-toggle-track" />
                        </label>
                      ) : (
                        <span className="kgd-em-dash">—</span>
                      )}
                    </td>

                    {/* Actions: primary CTA + overflow menu */}
                    <td className="kgd-cell-actions">
                      <div className="kgd-actions-inner">
                        <button
                          className="tab-btn tab-btn--ghost kgd-view-btn"
                          onClick={() => navigate(`/kg/${kgId}/version/${version.version_id}`)}
                        >
                          View details
                        </button>
                        <RowMenu
                          versionId={version.version_id}
                          published={version.published}
                          onPublishToggle={() => handlePublishToggle(version.version_id, version.published)}
                          onDelete={() => handleDeleteVersion(String(version.version_id))}
                        />
                      </div>
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showCreateModal && kgId && (
        <CreateVersionModal
          kgId={kgId}
          onClose={() => setShowCreateModal(false)}
          onCreated={handleVersionCreated}
          hasGdsLicense={hasGdsLicense}
        />
      )}

      {confirmDeleteVersionId && (
        <TypedConfirmModal
          title="Delete version"
          message="This action cannot be undone. Type the version ID below to confirm deletion."
          confirmWord={confirmDeleteVersionId}
          confirmLabel="Delete version"
          onConfirm={handleConfirmDelete}
          onCancel={() => setConfirmDeleteVersionId(null)}
        />
      )}
    </div>
  );
};

export default KGDetails;
